import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mockData } from '../data/mock';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Programs = () => {
  const location = useLocation();

  // Handle hash scrolling with offset for fixed header
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const headerOffset = 150; // Account for fixed header and banner
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
    } else {
      // Scroll to top only if no hash
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Custom content for Dream Sports Championship
  const dscContent = {
    title: 'Dream Sports Championship',
    paragraphs: [
      "DSF's flagship youth tournament platform, created to raise the standard of age-group competitions in India. It offers young athletes a high-quality competitive experience, combining international-standard match environments with advanced analytics and knowledge exchange workshops.",
      "Delivered in partnership with National Sports Federations, the Championship brings together top youth athletes from across India and international teams and players. The formats focus on meaningful match play, learning, and all-round development, supporting growth both on and off the field."
    ],
    featuresTitle: 'What Sets It Apart',
    features: [
      'Participation from international teams and players',
      'Cash rewards and attractive prizes with no entry fee',
      'Player and match analytics for performance insights',
      'Live streaming for wider reach and visibility',
      'Knowledge exchange workshops for athletes and coaches'
    ],
    tournamentLinks: [
      { label: 'Football 2025', url: 'https://dsf.dreamsports.group/dsc-football2025' },
      { label: 'Table Tennis 2025', url: 'https://youtu.be/NtZkQSwOSE4' },
      { label: 'Football 2024', url: 'https://dsf.dreamsports.group/dsc-football2024' }
    ]
  };

  // Custom content for Dream Again
  const dreamAgainContent = {
    title: 'Dream Again',
    paragraphs: [
      "Dream Again is DSF's upskilling program to build a skilled and sustainable sports ecosystem in India. Anchored in Upskilling, Education, and Employment Enhancement, it strengthens capability and creates clear career pathways for sports excellence.",
      "The program focuses on developing Tech and AI-literate coaches, emotionally intelligent mentors, and industry-ready subject matter experts. Through practical learning and ecosystem linkages, Dream Again bridges classroom knowledge to real-world sport delivery and employment outcomes."
    ],
    featuresTitle: 'Program Highlights',
    features: [
      'Tech and AI upskilling masterclasses',
      'Emotional intelligence workshops',
      'Industry readiness programs',
      'Role-specific upskilling modules',
      'Expert-led structured learning',
      'Academia and industry linkages'
    ]
  };

  // Custom content for Dream Sports MSSA Tournaments
  const mssaContent = {
    title: 'Dream Sports MSSA Tournaments',
    paragraphs: [
      "At DSF, we believe the future of Indian sport is shaped on school grounds. Through our partnership with the Mumbai Schools Sports Association (MSSA), we are strengthening Mumbai's school sports ecosystem by supporting professionally managed competitions that engage 50,000+ student-athletes across 400+ schools each year.",
      "For many students, these events are their first exposure to competitive sport. By ensuring a well-run and rewarding experience, we help identify and nurture talent early while building positive, lasting sporting habits."
    ],
    featuresTitle: "What's in it for student-athletes?",
    features: [
      'Enhanced competition infrastructure',
      'High-quality officiating standards',
      'Athlete recognition',
      'Live streaming and performance analytics'
    ]
  };

  return (
    <div className="programs-page">
      <section className="page-banner-section grunge-texture">
        <div className="page-banner-image">
          <img 
            src="/programs-banner.png" 
            alt="Dream Sports Foundation Programs"
            className="page-banner-img"
          />
        </div>
      </section>

      <section className="programs-detail-section grunge-texture">
        <div className="programs-container">
          <div className="section-header">
            <h2 className="heading-2">Our Programs</h2>
          </div>
          {mockData.programs.map((program, index) => (
            <div key={program.id} id={program.link.split('#')[1]} className="program-detail-card">
              {program.id === 1 ? (
                /* Custom layout for Dream Sports Championship */
                <div className="program-detail-grid dsc-layout">
                  <div className="program-detail-image dsc-image">
                    {program.image.endsWith('.mp4') ? (
                      <video src={program.image} poster={program.image.replace('.mp4', '-poster.jpg')} autoPlay loop muted playsInline />
                    ) : (
                      <img src={program.image} alt={program.title} />
                    )}
                  </div>
                  <div className="program-detail-content dsc-content">
                    <h2 className="heading-2">{dscContent.title}</h2>
                    {dscContent.paragraphs.map((para, idx) => (
                      <p key={idx} className="body-medium dsc-paragraph">{para}</p>
                    ))}
                    <div className="dsc-features-row">
                      <div className="program-features">
                        <h4 className="heading-5">{dscContent.featuresTitle}</h4>
                        <ul className="body-medium">
                          {dscContent.features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="dsc-tournament-links">
                        <h4 className="heading-5">Past Editions:</h4>
                        <ul className="body-medium">
                          {dscContent.tournamentLinks.map((link, idx) => (
                            <li key={idx}>
                              <a href={link.url} target="_blank" rel="noopener noreferrer">
                                {link.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : program.id === 2 ? (
                /* Custom layout for Dream Again - reversed (text left, image right) */
                <div className="program-detail-grid dsc-layout dsc-layout-reverse">
                  <div className="program-detail-content dsc-content">
                    <h2 className="heading-2">{dreamAgainContent.title}</h2>
                    {dreamAgainContent.paragraphs.map((para, idx) => (
                      <p key={idx} className="body-medium dsc-paragraph">{para}</p>
                    ))}
                    <div className="program-features">
                      <h4 className="heading-5">{dreamAgainContent.featuresTitle}</h4>
                      <ul className="body-medium">
                        {dreamAgainContent.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="program-detail-image dsc-image">
                    {program.image.endsWith('.mp4') ? (
                      <video src={program.image} poster={program.image.replace('.mp4', '-poster.jpg')} autoPlay loop muted playsInline />
                    ) : (
                      <img src={program.image} alt={program.title} />
                    )}
                  </div>
                </div>
              ) : program.id === 3 ? (
                /* Custom layout for MSSA Tournaments - image left, text right */
                <div className="program-detail-grid dsc-layout">
                  <div className="program-detail-image dsc-image">
                    {program.image.endsWith('.mp4') ? (
                      <video src={program.image} poster={program.image.replace('.mp4', '-poster.jpg')} autoPlay loop muted playsInline />
                    ) : (
                      <img src={program.image} alt={program.title} />
                    )}
                  </div>
                  <div className="program-detail-content dsc-content">
                    <h2 className="heading-2">{mssaContent.title}</h2>
                    {mssaContent.paragraphs.map((para, idx) => (
                      <p key={idx} className="body-medium dsc-paragraph">{para}</p>
                    ))}
                    <div className="program-features">
                      <h4 className="heading-5">{mssaContent.featuresTitle}</h4>
                      <ul className="body-medium">
                        {mssaContent.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                /* Standard layout for other programs */
                <div className={`program-detail-grid ${index % 2 === 1 ? 'reverse' : ''}`}>
                  <div className="program-detail-image">
                    <img src={program.image} alt={program.title} />
                  </div>
                  <div className="program-detail-content">
                    <h2 className="heading-2">{program.title}</h2>
                    <p className="body-large">{program.description}</p>
                    <div className="program-features">
                      <h4 className="heading-5">Key Features:</h4>
                      <ul className="body-medium">
                        <li>Structured training programs</li>
                        <li>Expert coaching and mentorship</li>
                        <li>Sports science support</li>
                        <li>Competitive opportunities</li>
                        <li>Career guidance and education</li>
                      </ul>
                    </div>
                    <Button className="btn-primary">Apply Now</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Programs;